import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import { safeGetJSON } from '@/lib/safeStorage';

interface SecretDiscoveryState {
  idleRoomUnlocked: boolean;
  visitVariants: Record<string, number>;
  unlockedVariants: string[];
  discoveredSecrets: string[];
}

const defaultState: SecretDiscoveryState = {
  idleRoomUnlocked: false,
  visitVariants: {},
  unlockedVariants: [],
  discoveredSecrets: [],
};

const IDLE_TIME_TO_UNLOCK = 120; // 2 minutes
const VISITS_TO_UNLOCK_VARIANT = 5;

export const useSecretDiscovery = () => {
  const [state, setState] = useState<SecretDiscoveryState>(() => {
    return safeGetJSON<SecretDiscoveryState>('human-rooms-secrets', defaultState);
  });

  useEffect(() => {
    localStorage.setItem('human-rooms-secrets', JSON.stringify(state));
  }, [state]);

  const trackRoomVisit = useCallback((roomId: string) => {
    setState(prev => {
      const newVisits = { ...prev.visitVariants };
      newVisits[roomId] = (newVisits[roomId] || 0) + 1;

      let newUnlockedVariants = [...prev.unlockedVariants];
      
      // Check if this room now has a variant unlocked
      if (newVisits[roomId] >= VISITS_TO_UNLOCK_VARIANT && !newUnlockedVariants.includes(roomId)) {
        newUnlockedVariants.push(roomId);
        toast({
          title: "Variant Unlocked",
          description: `You've visited "${roomId}" enough times. A deeper version awaits...`,
        });
      }

      return {
        ...prev,
        visitVariants: newVisits,
        unlockedVariants: newUnlockedVariants,
      };
    });
  }, []);

  const unlockIdleRoom = useCallback(() => {
    setState(prev => {
      if (!prev.idleRoomUnlocked) {
        return { ...prev, idleRoomUnlocked: true };
      }
      return prev;
    });
  }, []);

  const discoverSecret = useCallback((secretId: string) => {
    setState(prev => {
      if (!prev.discoveredSecrets.includes(secretId)) {
        toast({
          title: "Secret Discovered",
          description: "You found something hidden...",
        });
        return {
          ...prev,
          discoveredSecrets: [...prev.discoveredSecrets, secretId],
        };
      }
      return prev;
    });
  }, []);

  const hasVariant = useCallback((roomId: string) => {
    return state.unlockedVariants.includes(roomId);
  }, [state.unlockedVariants]);

  const getVisitCount = useCallback((roomId: string) => {
    return state.visitVariants[roomId] || 0;
  }, [state.visitVariants]);

  return {
    state,
    trackRoomVisit,
    unlockIdleRoom,
    discoverSecret,
    hasVariant,
    getVisitCount,
    IDLE_TIME_TO_UNLOCK,
    VISITS_TO_UNLOCK_VARIANT,
  };
};

// Global idle detection hook
export const useIdleDetection = (onIdle: () => void, timeoutMs: number = IDLE_TIME_TO_UNLOCK * 1000) => {
  useEffect(() => {
    let idleTimer: NodeJS.Timeout;
    let hasTriggered = false;

    const resetTimer = () => {
      clearTimeout(idleTimer);
      if (!hasTriggered) {
        idleTimer = setTimeout(() => {
          hasTriggered = true;
          onIdle();
        }, timeoutMs);
      }
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, resetTimer));
    
    // Start timer immediately
    resetTimer();

    return () => {
      clearTimeout(idleTimer);
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [onIdle, timeoutMs]);
};